'use client'

import React, { ReactNode, useState } from 'react'
import useInputWrapper from '../../../hooks/useInputWrapper'
import { ChangeHandler } from 'react-hook-form'

export interface TextAreaProps
    extends Pick<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'defaultValue' | 'minLength' | 'maxLength'> {
    id: string
    form?: string
    name: string
    rows?: number
    placeholder?: string
    required?: boolean
    disabled?: boolean
    readOnly?: boolean
    resizable?: boolean
    maxChar?: number
    customCount?: string
    error?: boolean
    onChange?: (value: string) => void
    onBlur?: ChangeHandler
    value?: string
    autoFocus?: boolean
}

export const Textarea = ({
    id,
    form,
    name,
    rows = 4,
    placeholder = '',
    required = false,
    disabled = false,
    readOnly = false,
    resizable = true,
    maxChar,
    customCount,
    error,
    onChange,
    onBlur,
    value,
    defaultValue,
    autoFocus = false,
    ...rest
}: TextAreaProps) => {
    const [count, setCount] = useState(
        value?.length ?? (typeof defaultValue === 'string' ? defaultValue?.length : undefined) ?? 0,
    )
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)
    const maxLength = rest.maxLength ?? maxChar

    return (
        <div className={`relative border flex ${classes}`} data-cy="input-text-area">
            <textarea
                {...rest}
                id={id || name}
                form={form}
                name={name}
                rows={rows}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                readOnly={readOnly}
                className={`bg-transparent outline-0 w-full p-3 pr-12 placeholder:italic ${
                    !resizable ? 'resize-none' : ''
                }`}
                maxLength={maxLength}
                onFocus={onFocusHandler}
                onChange={(e) => {
                    setCount(e.target.value.length)
                    onChange?.(e.target.value)
                }}
                onBlur={(e) => {
                    onBlur && onBlur(e)
                    onBlurHandler()
                }}
                value={value}
                defaultValue={defaultValue}
                autoFocus={autoFocus}
                aria-invalid={error}
            />

            {maxLength && !readOnly && !customCount && (
                <Counter>
                    {count}/{maxLength}
                </Counter>
            )}

            {customCount && <Counter>{customCount}</Counter>}
        </div>
    )
}

const Counter = ({ children }: { children: ReactNode }): JSX.Element => (
    <span className="absolute right-4 bottom-4 text-shade-600">{children}</span>
)
